import React, { Component, createRef } from 'react';
import * as R from 'ramda';
import { Fieldset, RequiredAsterisk } from '@atlaskit/form';
import { N300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { injectIntl } from '@atlassian/jira-intl';
import {
	AkSelect,
	AkTextField,
	ModalDialog,
	ModalHeader,
	ModalTitle,
	ModalBody,
	ModalFooter,
	StandardButton,
	ErrorMessage,
	RadioGroup,
	Field,
	Form,
	type OptionType,
	type AKSelectProps,
} from '@atlassian/jira-portfolio-3-common';
import { isDefined } from '@atlassian/jira-portfolio-3-portfolio/src/common/ramda';
import ColourPicker from '@atlassian/jira-portfolio-3-portfolio/src/common/view/colour-picker';
import { colourPickerPalette } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/colours';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';

import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { PropsWithIntl as Props, ScenarioPayload, State } from './types';

const SCENARIO_TYPES = {
	BLANK: 'blank',
	COPY: 'copy',
} as const;
// eslint-disable-next-line jira/react/no-class-components
export class ScenarioDialog extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			isScenarioTitleFieldValid: false, // defaulted to false since the field is required
		};
	}

	buttonRef = createRef<HTMLButtonElement>();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	submitForm = (fields: any) => {
		const { scenarioTitle, scenarioColour } = fields;
		const payload: ScenarioPayload = { title: scenarioTitle.trim(), color: scenarioColour };
		// if fields.scenarioType is undefined, it means that the user is enabling scenarios in the plan
		// we then need to create a scenario which is a "copy" of the current scenario, although it is not technically a copy
		if (!fields.scenarioType) {
			payload.scenarioId = this.props.currentScenarioId;
		} else if (fields.scenarioType === SCENARIO_TYPES.COPY) {
			payload.scenarioId = fields.scenarioId.value;
		}
		this.props.createScenario(payload);
	};

	setScenarioTitleFieldValidity = (isValid: boolean) => {
		this.setState({ isScenarioTitleFieldValid: isValid });
	};

	validateScenarioTitle = (scenarioTitle: string | undefined) => {
		const {
			intl: { formatMessage },
		} = this.props;

		if (!isDefined(scenarioTitle)) {
			this.setScenarioTitleFieldValidity(false);
			return '';
		}
		if (scenarioTitle.trim() === '') {
			this.setScenarioTitleFieldValidity(false);
			return formatMessage(messages.scenarioTitleEmpty);
		}
		if (scenarioTitle.length > 255) {
			this.setScenarioTitleFieldValidity(false);
			return formatMessage(messages.titleLengthValidation);
		}
		this.setScenarioTitleFieldValidity(true);
		return '';
	};

	getDefaultScenarioOption = () =>
		this.getScenarioOptions().find(({ value }) => value === this.props.currentScenarioId);

	getScenarioOptions = () => {
		const {
			currentScenarioId,
			intl: { formatMessage },
			planScenarios = [],
		} = this.props;

		return planScenarios.map(({ id, title }) => ({
			label: `${title} ${
				id === currentScenarioId ? formatMessage(messages.currentScenarioLabel) : ''
			}`,
			value: id,
		}));
	};

	getDefaultColour = () => {
		const { planScenarios = [] } = this.props;
		const colors = R.difference(
			colourPickerPalette,
			planScenarios.map(({ color }) => color),
		);
		return colors[0] || colourPickerPalette[0];
	};

	render() {
		const {
			intl: { formatMessage },
			isMultiScenarioEnabled = false,
			isProcessingRequest,
			onClose,
			planScenarios = [],
		} = this.props;
		const { isScenarioTitleFieldValid } = this.state;

		return (
			<ModalDialog
				autoFocus
				onClose={onClose}
				shouldCloseOnEscapePress={!isProcessingRequest}
				shouldCloseOnOverlayClick={!isProcessingRequest}
			>
				<ModalHeader>
					<ModalTitle>
						{formatMessage(
							isMultiScenarioEnabled ? messages.dialogTitle : messages.dialogTitleFirstScenario,
						)}
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<p
						style={{
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							color: token('color.text.subtle', N300),
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							marginBottom: token('space.300', '24px'),
						}}
					>
						{formatMessage(messages.requiredFieldsSummary)}
						<RequiredAsterisk />
					</p>
					{!isMultiScenarioEnabled && formatMessage(messages.firstScenarioDescription)}
					<Form onSubmit={this.submitForm}>
						{({ formProps, getState }) => (
							<form {...formProps}>
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<div className={styles.scenarioTitleAndColourFields}>
									{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
									<div className={styles.scenarioTitle}>
										<Field
											aria-required
											defaultValue={isMultiScenarioEnabled ? undefined : planScenarios[0].title}
											isDisabled={isProcessingRequest}
											isRequired
											label={formatMessage(messages.scenarioTitle)}
											name="scenarioTitle"
											validate={this.validateScenarioTitle}
										>
											{({ fieldProps, meta }) => (
												<>
													<AkTextField
														{...fieldProps}
														testId="portfolio-3-portfolio.app-simple-plans.settings.scenarios.scenario-dialog.title"
														onBlur={(value) => fieldProps.onChange(value || '')}
														placeholder={formatMessage(messages.scenarioTitlePlaceholder)}
													/>
													{isDefined(meta.error) && <ErrorMessage>{meta.error}</ErrorMessage>}
												</>
											)}
										</Field>
									</div>
									<div
										aria-label={formatMessage(messages.scenarioColor)}
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
										className={styles.scenarioColour}
									>
										<Field
											defaultValue={this.getDefaultColour()}
											isDisabled={isProcessingRequest}
											name="scenarioColour"
										>
											{({ fieldProps }) => (
												<ColourPicker
													colour={fieldProps.value}
													onColourChange={(colour) => {
														isDefined(colour) && fieldProps.onChange(colour);
													}}
													position="bottom-start"
													selectedColourSwatchSize="default"
												/>
											)}
										</Field>
									</div>
								</div>
								{isMultiScenarioEnabled && (
									<>
										{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
										<div className={styles.radioGroup}>
											<Fieldset legend={formatMessage(messages.scenarioCreateFrom)}>
												<Field
													defaultValue={SCENARIO_TYPES.COPY}
													isDisabled={isProcessingRequest}
													name="scenarioType"
												>
													{({ fieldProps }) => (
														<RadioGroup
															onChange={fieldProps.onChange}
															options={[
																{
																	label: formatMessage(messages.createFromBlank),
																	value: SCENARIO_TYPES.BLANK,
																},
																{
																	label: formatMessage(messages.createFromScenario),
																	value: SCENARIO_TYPES.COPY,
																},
															]}
															value={fieldProps.value}
														/>
													)}
												</Field>
											</Fieldset>
										</div>
										<Field
											defaultValue={this.getDefaultScenarioOption()}
											isDisabled={
												getState().values.scenarioType === SCENARIO_TYPES.BLANK ||
												isProcessingRequest
											}
											name="scenarioId"
										>
											{({ fieldProps }: AKSelectProps<OptionType>) => (
												<AkSelect
													{...fieldProps}
													menuPortalTarget={document.body}
													options={this.getScenarioOptions()}
													styles={{
														menuPortal: (base) => ({
															...base,
															zIndex: 9999,
														}),
													}}
												/>
											)}
										</Field>
									</>
								)}
								{/**
								 * This button below is added to create a bridge between the form and the submit button in the modal footer.
								 * When the button in the modal footer is clicked, it instantiates a click on this button which leads to form submit action.
								 * This is done since only a button of type submit triggers the submission of the form, and the modal footer buttons are
								 * not wrapped within the <form> - they are rendered outside of it.
								 */}
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
								<div className={styles.hiddenSubmitButton}>
									<button type="submit" ref={this.buttonRef} />
								</div>
							</form>
						)}
					</Form>
				</ModalBody>
				<ModalFooter>
					<StandardButton appearance="subtle" isDisabled={isProcessingRequest} onClick={onClose}>
						{formatMessage(commonMessages.cancel)}
					</StandardButton>
					<StandardButton
						appearance="primary"
						isDisabled={!isScenarioTitleFieldValid || isProcessingRequest}
						onClick={() => {
							if (this.buttonRef.current) {
								this.buttonRef.current.click();
							}
						}}
					>
						{formatMessage(commonMessages.create)}
					</StandardButton>
				</ModalFooter>
			</ModalDialog>
		);
	}
}

export default injectIntl(ScenarioDialog);
